import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { Refresh, Search } from '@mui/icons-material';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useCreateFeedComment, useCreateFeedEntry, useGetFeedEntries } from '~/hooks/apis/contracting/useFeedEntries';
import useCurrentUser from '~/hooks/useCurrentUser';
import useDebounced from '~/hooks/useDebounced';

import { FeedParentType } from '~/types/contracting/feed';

import { salesforceRecordUrl } from '../Assets/utils';

import ChatterComposer from './ChatterComposer';
import ChatterPostCard from './ChatterPostCard';
import ChatterUpdateGroupCard from './ChatterUpdateGroupCard';
import { CHATTER_SORT_OPTIONS, SALESFORCE_OBJECT_BY_PARENT_TYPE } from './constants';
import { ChatterDraft, ChatterSortOrder } from './types';
import { toChatterPost } from './utils';

const FALLBACK_AUTHOR_NAME = 'Admin user';
const SEARCH_DELAY_MS = 400;
// Salesforce matches nothing for a single character.
const MIN_SEARCH_LENGTH = 2;
const QUEUED_MESSAGE = 'Sent to Salesforce. It will show here in a few seconds.';

interface Props {
  parentType: FeedParentType;
  parentId: string;
  scopeLabel: string;
}

export default function ChatterFeed({ parentType, parentId, scopeLabel }: Props) {
  const { user } = useCurrentUser();
  const currentUserName = user?.fullName || FALLBACK_AUTHOR_NAME;
  const { enqueueSnackbar } = useSnackbar();

  const [sortOrder, setSortOrder] = useState<ChatterSortOrder>('activity');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounced(searchQuery.trim(), SEARCH_DELAY_MS);
  const filter = debouncedSearch.length >= MIN_SEARCH_LENGTH ? debouncedSearch : undefined;
  const trimmedSearchLength = searchQuery.trim().length;
  const isSearchTooShort = trimmedSearchLength > 0 && trimmedSearchLength < MIN_SEARCH_LENGTH;

  const feed = useGetFeedEntries({
    parent_type: parentType,
    parent_salesforce_id: parentId,
    sort: sortOrder,
    filter,
  });
  const createEntry = useCreateFeedEntry(parentType, parentId);
  const createComment = useCreateFeedComment(parentType, parentId);
  const [commentingPostIds, setCommentingPostIds] = useState<ReadonlySet<string>>(new Set());

  const send = async (action: string, write: () => Promise<string>) => {
    try {
      await write();
      enqueueSnackbar(QUEUED_MESSAGE, { variant: 'success' });
      return true;
    } catch (error) {
      enqueueSnackbar(`Failed to ${action}: ${error.message}`, { variant: 'error' });
      return false;
    }
  };

  const toPayload = ({ text, mentions, idempotencyKey }: ChatterDraft) => ({
    parent_type: parentType,
    parent_salesforce_id: parentId,
    body: text,
    mentions,
    idempotency_key: idempotencyKey,
  });

  const handleShare = (draft: ChatterDraft) => send('post', () => createEntry.mutateAsync(toPayload(draft)));

  const handleAddComment = async (postId: string, draft: ChatterDraft) => {
    setCommentingPostIds((prev) => new Set(prev).add(postId));
    try {
      return await send('comment', () => createComment.mutateAsync({ feedEntryId: postId, payload: toPayload(draft) }));
    } finally {
      setCommentingPostIds((prev) => {
        const next = new Set(prev);
        next.delete(postId);
        return next;
      });
    }
  };

  const posts = feed.entries.map(toChatterPost);
  const salesforceUrl = salesforceRecordUrl(SALESFORCE_OBJECT_BY_PARENT_TYPE[parentType], parentId);

  const renderFeedContent = () => {
    if (feed.isPending) {
      return (
        <Stack alignItems="center" py={4}>
          <CircularProgress />
        </Stack>
      );
    }
    // A failed refresh or "Load more" keeps the posts already shown.
    if (feed.isError && !feed.data) {
      return <Alert severity="error">Failed to load the Salesforce feed: {feed.error.message}</Alert>;
    }
    if (posts.length === 0) {
      return (
        <Typography color="text.secondary" align="center" py={4}>
          {filter ? 'No posts match that search.' : 'No posts yet. Share an update to start the conversation.'}
        </Typography>
      );
    }
    return (
      <>
        {feed.isError && <Alert severity="warning">Could not refresh the Salesforce feed: {feed.error.message}</Alert>}
        {posts.map((post) =>
          post.kind === 'record_update_group' ? (
            <ChatterUpdateGroupCard key={post.id} post={post} salesforceUrl={salesforceUrl} />
          ) : (
            <ChatterPostCard
              key={post.id}
              post={post}
              currentUserName={currentUserName}
              isSendingComment={commentingPostIds.has(post.id)}
              salesforceUrl={salesforceUrl}
              onAddComment={handleAddComment}
            />
          ),
        )}
        {feed.hasNextPage && (
          <Button onClick={() => feed.fetchNextPage()} disabled={feed.isFetchingNextPage}>
            {feed.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        )}
      </>
    );
  };

  return (
    <Stack spacing={2} maxWidth={980}>
      <ChatterComposer scopeLabel={scopeLabel} isSending={createEntry.isPending} onShare={handleShare} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">Sort by:</Typography>
          <TextField
            select
            size="small"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value as ChatterSortOrder)}
            slotProps={{ htmlInput: { 'aria-label': 'Sort feed' } }}
          >
            {CHATTER_SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TextField
            type="search"
            size="small"
            placeholder="Search this feed..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: isSearchTooShort && (
                  <InputAdornment position="end">
                    <Typography variant="caption" color="text.secondary">
                      {MIN_SEARCH_LENGTH}+ characters
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
          />
          <IconButton aria-label="Refresh" onClick={() => feed.refetch()} disabled={feed.isFetching}>
            <Refresh />
          </IconButton>
        </Stack>
      </Stack>
      {renderFeedContent()}
    </Stack>
  );
}
